import * as fflate from 'fflate';
import { unflattenToSlices } from '~/domain/sdfCompression';
import {
    getContourUndoService,
    getRoiBaselineStore,
} from '~/domain/ServiceContainer';
import { Grid } from '~/engine/data/common/Grid';
import { ComparisonOverlayRegion } from '~/engine/data/common/customs/ComparisonObject';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import {
    ADDED_COLOR,
    DIFF_FILL_ALPHA,
    REMOVED_COLOR,
} from '~/engine/core/render/renderers/util/crossSliceDiff';
import {
    BooleanOperation,
    SDFOperations,
} from '~/engine/util/algorithms/sdf/SDFOperations';
import { DifferenceReferenceMode } from '~/state/atoms/toolsAtoms';

/** Edge thickness for the added/removed difference fills. */
const DIFF_EDGE_THICKNESS = 2.5;

/**
 * Split a decompressed SDF snapshot buffer back into per-slice `Uint8Array`s,
 * mirroring `RegionOfInterest.restorePixelsFromCompressed`. Pure — unit tested.
 */
export function decompressSnapshotToSlices(
    compressedPixels: Uint8Array,
    grid: Grid
): Uint8Array[] {
    const decompressed = fflate.decompressSync(compressedPixels);
    return unflattenToSlices(decompressed, grid.sizeX, grid.sizeY, grid.sizeZ);
}

/**
 * Rebuild an independent reference `ContourTexture` from a compressed SDF
 * snapshot (undo history or an AI baseline). `template` supplies the GPU
 * scaffolding to clone; the result carries the snapshot's own pixels + grid and
 * is owned by the caller (delete it when done).
 */
export function referenceTextureFromSnapshot(
    template: ContourTexture,
    compressedPixels: Uint8Array,
    grid: Grid
): ContourTexture {
    return template.cloneWithNewSdf(
        decompressSnapshotToSlices(compressedPixels, grid),
        { grid }
    );
}

/**
 * Resolve the reference texture for a SAME-slice difference overlay of `roi`:
 *  - 'pre-operation': the ROI's snapshot before its most recent edit (undo peek)
 *  - 'original-ai':   the ROI's Contour+ baseline, captured on its first edit
 *
 * 'previous-slice'/'off' are not same-slice references (the former is the engine
 * shader) and return undefined. The caller owns and must delete the result.
 */
export function resolveReferenceTexture(
    mode: DifferenceReferenceMode,
    roi: RegionOfInterest
): ContourTexture | undefined {
    const template = roi.texture;
    if (!template) {
        return undefined;
    }
    // Both same-slice references are a compressed SDF snapshot of the ROI: the
    // undo peek for 'pre-operation', the captured Contour+ baseline for
    // 'original-ai'. Other modes carry no same-slice reference here.
    let snapshot: { compressedPixels: Uint8Array; grid: Grid } | undefined;
    if (mode === 'pre-operation') {
        snapshot = getContourUndoService().peekPrevious(roi.roiId);
    } else if (mode === 'original-ai') {
        snapshot = getRoiBaselineStore().get(roi.roiId);
    }
    return snapshot
        ? referenceTextureFromSnapshot(
              template,
              snapshot.compressedPixels,
              snapshot.grid
          )
        : undefined;
}

/**
 * Build the added/removed overlay regions comparing `currentTex` to
 * `referenceTex` at the same slice: green (`ADDED_COLOR`) where the ROI is
 * present now but not in the reference, vermillion (`REMOVED_COLOR`) where
 * present in the reference but not now. Reuses the cross-slice colours so the
 * reference modes read identically to the previous-slice mode. Returns fresh
 * textures the caller / `ComparisonObject` owns and frees.
 */
export function buildReferenceDiffRegions(
    currentTex: ContourTexture,
    referenceTex: ContourTexture
): ComparisonOverlayRegion[] {
    const added = SDFOperations.applyBooleanUncropped(
        currentTex,
        referenceTex,
        BooleanOperation.AND_NOT
    );
    const removed = SDFOperations.applyBooleanUncropped(
        referenceTex,
        currentTex,
        BooleanOperation.AND_NOT
    );
    return [
        {
            texture: added,
            color: ADDED_COLOR,
            fillAlpha: DIFF_FILL_ALPHA,
            thickness: DIFF_EDGE_THICKNESS,
        },
        {
            texture: removed,
            color: REMOVED_COLOR,
            fillAlpha: DIFF_FILL_ALPHA,
            thickness: DIFF_EDGE_THICKNESS,
        },
    ];
}
